import { useQuery } from "@tanstack/react-query";
import type {
  MyPermissionsDetailed,
  Permission,
  PermissionCategory,
} from "gsintegrations";
import { permissionRepository } from "../repositories/permissionRepository";
import { useCachedPermissionContext } from "./useCachedPermissionContext";

/**
 * Query key factory for permissions
 */
export const permissionQueryKeys = {
  all: (terreiroId: string) => ["terreiro", terreiroId, "permissions"] as const,
  byCategory: (terreiroId: string, category: PermissionCategory) =>
    ["terreiro", terreiroId, "permissions", "category", category] as const,
  myPermissions: (
    scopeOrTerreiroId: "terreiro" | "personal" | string,
    scopeId?: string,
  ) => {
    if (scopeOrTerreiroId === "terreiro" || scopeOrTerreiroId === "personal") {
      return [
        "my-permissions",
        scopeOrTerreiroId,
        scopeId ?? "default",
      ] as const;
    }

    return [
      "my-permissions",
      "terreiro",
      scopeOrTerreiroId,
      scopeId ?? "default",
    ] as const;
  },
  myPermissionsDetailed: (
    scopeOrTerreiroId: "terreiro" | "personal" | string,
    scopeId?: string,
  ) => {
    if (scopeOrTerreiroId === "terreiro" || scopeOrTerreiroId === "personal") {
      return [
        "my-permissions",
        scopeOrTerreiroId,
        "detailed",
        scopeId ?? "default",
      ] as const;
    }

    return [
      "my-permissions",
      "terreiro",
      "detailed",
      scopeOrTerreiroId,
      scopeId ?? "default",
    ] as const;
  },
};

/**
 * Hook to get all permissions for a terreiro context
 */
interface UsePermissionsQueryOptions {
  terreiroId: string;
  enabled?: boolean;
}

export const usePermissionsQuery = ({
  terreiroId,
  enabled = true,
}: UsePermissionsQueryOptions) => {
  return useQuery<Permission[]>({
    queryKey: permissionQueryKeys.all(terreiroId),
    queryFn: () => permissionRepository.getAll(terreiroId),
    enabled: enabled && !!terreiroId,
    staleTime: 1000 * 60 * 30, // 30 minutes - permissions rarely change
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 2,
    refetchOnMount: false, // Permissions don't change often
  });
};

/**
 * Hook to get permissions by category for a terreiro context
 */
interface UsePermissionsByCategoryOptions {
  terreiroId: string;
  category: PermissionCategory;
  enabled?: boolean;
}

export const usePermissionsByCategoryQuery = ({
  terreiroId,
  category,
  enabled = true,
}: UsePermissionsByCategoryOptions) => {
  return useQuery<Permission[]>({
    queryKey: permissionQueryKeys.byCategory(terreiroId, category),
    queryFn: () => permissionRepository.getByCategory(terreiroId, category),
    enabled: enabled && !!terreiroId && !!category,
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 2,
    refetchOnMount: false,
  });
};

/**
 * Hook to get current user's permissions for a terreiro
 * Supports both terreiro and personal (medium) contexts.
 * Automatically detects context from cached data if not explicitly provided.
 */
interface UseMyPermissionsQueryOptions {
  terreiroId?: string;
  mediumId?: string;
  enabled?: boolean;
}

export const useMyPermissionsQuery = ({
  terreiroId: overrideTerreiroId,
  mediumId: overrideMediumId,
  enabled = true,
}: UseMyPermissionsQueryOptions = {}) => {
  const ctx = useCachedPermissionContext();

  const hasTerreiroOverride = overrideTerreiroId !== undefined;
  const hasMediumOverride = overrideMediumId !== undefined;

  // Precedence:
  // 1) explicit terreiro override
  // 2) explicit medium override (forces personal scope)
  // 3) cached context fallback
  const terreiroId = hasTerreiroOverride
    ? overrideTerreiroId || undefined
    : hasMediumOverride
      ? undefined
      : ctx.terreiroId || undefined;

  const mediumId = hasTerreiroOverride
    ? hasMediumOverride
      ? overrideMediumId
      : undefined
    : hasMediumOverride
      ? overrideMediumId
      : ctx.mediumId;
  const isPersonalScope =
    !terreiroId && (ctx.isPersonalProfile || !!mediumId || hasMediumOverride);
  const scope: "terreiro" | "personal" = terreiroId ? "terreiro" : "personal";
  const scopeId = terreiroId ?? mediumId;

  return useQuery<string[]>({
    queryKey: permissionQueryKeys.myPermissions(scope, scopeId),
    queryFn: () =>
      terreiroId
        ? permissionRepository.getMyPermissions(terreiroId)
        : permissionRepository.getMyPersonalPermissions(),
    enabled: enabled && (!!terreiroId || isPersonalScope),
    staleTime: 1000 * 60 * 5, // 5 minutes - user permissions might change
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 2,
    refetchOnMount: "always", // Check on mount for permission changes
  });
};

/**
 * Hook to get detailed permissions for current user in a terreiro
 * Returns full permission details with role information and categorization.
 * Supports both terreiro and personal (medium) contexts.
 * Automatically detects context from cached data if not explicitly provided.
 */
interface UseMyPermissionsDetailedQueryOptions {
  terreiroId?: string;
  mediumId?: string;
  enabled?: boolean;
}

export const useMyPermissionsDetailedQuery = ({
  terreiroId: overrideTerreiroId,
  mediumId: overrideMediumId,
  enabled = true,
}: UseMyPermissionsDetailedQueryOptions = {}) => {
  const ctx = useCachedPermissionContext();

  const hasTerreiroOverride = overrideTerreiroId !== undefined;
  const hasMediumOverride = overrideMediumId !== undefined;

  // Precedence:
  // 1) explicit terreiro override
  // 2) explicit medium override (forces personal scope)
  // 3) cached context fallback
  const terreiroId = hasTerreiroOverride
    ? overrideTerreiroId || undefined
    : hasMediumOverride
      ? undefined
      : ctx.terreiroId || undefined;

  const mediumId = hasTerreiroOverride
    ? hasMediumOverride
      ? overrideMediumId
      : undefined
    : hasMediumOverride
      ? overrideMediumId
      : ctx.mediumId;
  const isPersonalScope =
    !terreiroId && (ctx.isPersonalProfile || !!mediumId || hasMediumOverride);
  const scope: "terreiro" | "personal" = terreiroId ? "terreiro" : "personal";
  const scopeId = terreiroId ?? mediumId;

  return useQuery<MyPermissionsDetailed>({
    queryKey: permissionQueryKeys.myPermissionsDetailed(scope, scopeId),
    queryFn: () =>
      terreiroId
        ? permissionRepository.getMyPermissionsDetailed(terreiroId)
        : permissionRepository.getMyPersonalPermissionsDetailed(),
    enabled: enabled && (!!terreiroId || isPersonalScope),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 2,
    refetchOnMount: "always", // Check on mount for permission changes
  });
};
